import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";

import {
  KeyValueList,
  NoData,
  RenderWhen,
  Time
} from "../../../../../../../components";
import TagsEditSingle from "../../../../../../../components/tags/containers/TagsEditSingle";
import Hashes from "./Hashes";
import TagList from "./TagList";
import FileOriginList from "./FileOriginList";

import useModal from "../../../../../../../hooks/useModal";
import {
  useGetReportSummary,
  useLocalSampleSummaryParams
} from "../../../../../hooks";
import { SAMPLE_REPORT_KEY } from "../../../../../hooks/useGetReportSummary";
import { PDFExport } from "../../../@types/PDFExport";

import SearchLink from "../../../../../../search/components/links/SearchLink";
import styles from "../info.module.scss";

type Props = PDFExport;

const InfoContent = ({ isPdfView }: Props) => {
  const queryClient = useQueryClient();

  const [isModalOpen, , toggleIsModalOpen] = useModal();
  const { hash, sourceId, relationshipId } = useLocalSampleSummaryParams();
  const { data } = useGetReportSummary(hash, sourceId, relationshipId);

  return (
    <>
      <KeyValueList className="flex flex-column flex-gap-2 overflow-hidden">
        <Hashes
          sha1={data?.sha1}
          sha256={data?.sha256}
          sha512={data?.sha512}
          imphash={data?.imphash}
          md5={data?.md5}
          ssdeep={data?.ssdeep}
          tlsh={data?.tlsh}
          isPdfView={isPdfView}
        />
        <KeyValueList.Item
          className="flex k-pdf-info-item"
          keyClassName="white-space-nowrap"
          label="First Seen Cloud:"
          value={
            <RenderWhen
              condition={Boolean(data?.first_seen_cloud)}
              fallback={<NoData />}
            >
              <Time time={data?.first_seen_cloud} />
            </RenderWhen>
          }
          data-testid="sample_information_first_seen_cloud"
        />
        <KeyValueList.Item
          className="flex k-pdf-info-item"
          keyClassName="white-space-nowrap"
          label="Last Seen Cloud:"
          value={
            <RenderWhen
              condition={Boolean(data?.last_seen_cloud)}
              fallback={<NoData />}
            >
              <Time time={data?.last_seen_cloud} />
            </RenderWhen>
          }
          data-testid="sample_information_last_seen_cloud"
        />
        <KeyValueList.Item
          className="flex k-pdf-info-item"
          keyClassName="white-space-nowrap"
          label={`File Names (${data?.file_names?.length ?? 0}):`}
          value={
            <TagList
              data={data?.file_names ?? []}
              renderTag={(item: string, className?: string) => (
                <span key={item} className={clsx(styles.item_link, className)}>
                  <FileOriginList
                    originList={data?.file_origin_dirs[item] || []}
                  />
                  <SearchLink
                    keyword={"FILENAME"}
                    value={item}
                    data-testid={`sample_information_file_name_link_${item}`}
                  />
                </span>
              )}
              data-testid="sample_information_file_names"
            />
          }
          data-testid="sample_information_file_names_container"
        />
        <KeyValueList.Item
          className="flex k-pdf-info-item"
          keyClassName="white-space-nowrap"
          label={`Uploads (${data?.owners?.length ?? 0}):`}
          value={
            <TagList
              data={data?.owners ?? []}
              renderTag={(item: string, className?: string) => (
                <SearchLink
                  key={item}
                  value={item}
                  keyword="SUBMISSION_USER"
                  className={clsx(styles.item_link, className)}
                  data-testid={`sample_information_uploads_user_link_${item}`}
                />
              )}
              data-testid="sample_information_uploads"
            />
          }
          data-testid="sample_information_uploads_container"
        />
        <KeyValueList.Item
          className="flex k-pdf-info-item"
          keyClassName="white-space-nowrap"
          label={`User Tags (${data?.tags_user.length}):`}
          value={
            <TagList
              data={data?.tags_user ?? []}
              renderTag={(item: string, className?: string) => (
                <SearchLink
                  key={item}
                  keyword="TAG_USER"
                  value={item}
                  className={clsx(styles.item_link, className)}
                  data-testid={`sample_information_user_tags_link_${item}`}
                />
              )}
              data-testid="sample_information_user_tags"
            >
              <span
                className={styles.add_more}
                onClick={toggleIsModalOpen}
                data-testid="sample_information_edit_tags_link"
              >
                Edit Tags
              </span>
            </TagList>
          }
          data-testid="sample_information_user_tags_container"
        />
        <KeyValueList.Item
          className="flex k-pdf-info-item"
          keyClassName="white-space-nowrap"
          label={`System Tags (${data?.tags_system.length}):`}
          value={
            <TagList
              data={data?.tags_system ?? []}
              renderTag={(item: string, className?: string) => (
                <SearchLink
                  key={item}
                  keyword="TAG"
                  value={item}
                  className={clsx(styles.item_link, className)}
                  data-testid={`sample_information_system_tags_link_${item}`}
                />
              )}
              data-testid="sample_information_system_tags"
            />
          }
          data-testid="sample_information_system_tags_container"
        />
      </KeyValueList>

      <RenderWhen condition={hash && isModalOpen}>
        <TagsEditSingle
          hash={hash ?? ""}
          show={isModalOpen}
          onSubmitCallback={() =>
            queryClient.refetchQueries(
              { queryKey: [SAMPLE_REPORT_KEY] },
              { cancelRefetch: false }
            )
          }
          handleShow={toggleIsModalOpen}
        />
      </RenderWhen>
    </>
  );
};

export default InfoContent;
